/**
 * Push notification helpers for PWA.
 *
 * Handles:
 *  - Checking browser support
 *  - Requesting notification permission
 *  - Subscribing to push via the Push API
 *  - Sending the subscription to the backend
 */

import { apiUrl } from '@/config'
import { getAuthHeaders } from '@/stores/authStore'

/** Check if push notifications are supported in this browser */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/** Get the current notification permission state */
export function getPermissionState(): NotificationPermission {
  if (!('Notification' in window)) return 'denied'
  return Notification.permission
}

/** Fetch the VAPID public key from the backend */
async function getVapidPublicKey(): Promise<string> {
  const resp = await fetch(apiUrl('/api/push/vapid-key'))
  if (!resp.ok) throw new Error('Failed to get VAPID key')
  const data = await resp.json()
  return data.publicKey
}

/** Convert a base64url string to Uint8Array (for applicationServerKey) */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/**
 * Subscribe to push notifications for a shop.
 * 1. Request notification permission
 * 2. Get SW registration
 * 3. Subscribe via Push API
 * 4. Send subscription to backend
 */
export async function subscribeToPush(shopId: string): Promise<boolean> {
  if (!isPushSupported()) {
    console.warn('[Push] Not supported in this browser')
    return false
  }

  // 1. Request permission
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    console.warn('[Push] Permission denied')
    return false
  }

  // 2. Get service worker registration
  const registration = await navigator.serviceWorker.ready
  if (!registration.pushManager) {
    console.warn('[Push] PushManager not available')
    return false
  }

  // 3. Get VAPID key and subscribe
  const vapidKey = await getVapidPublicKey()
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey),
  })

  // 4. Send to backend
  const resp = await fetch(apiUrl('/api/push/subscribe'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({
      shopId,
      subscription: subscription.toJSON(),
    }),
  })

  if (!resp.ok) {
    console.error('[Push] Failed to save subscription on server')
    return false
  }

  console.log('[Push] Subscribed successfully for shop:', shopId)
  return true
}

/**
 * Unsubscribe from push notifications for a shop.
 */
export async function unsubscribeFromPush(shopId: string): Promise<boolean> {
  try {
    // Unsubscribe from Push API
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) {
      await subscription.unsubscribe()
    }

    // Remove from backend
    await fetch(apiUrl(`/api/push/subscribe/${shopId}`), {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    })

    console.log('[Push] Unsubscribed for shop:', shopId)
    return true
  } catch (err) {
    console.error('[Push] Unsubscribe failed:', err)
    return false
  }
}

/**
 * Check if we currently have an active push subscription.
 */
export async function isSubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    return subscription !== null
  } catch {
    return false
  }
}

// ── Customer push subscription (user-level, not shop-specific) ────

/**
 * Subscribe the current user to push notifications (customer-level).
 * Used for order status updates and chat messages.
 */
export async function subscribeCustomerPush(): Promise<boolean> {
  if (!isPushSupported()) {
    console.warn('[Push] Not supported')
    return false
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    console.warn('[Push] Permission denied')
    return false
  }

  const registration = await navigator.serviceWorker.ready
  if (!registration.pushManager) return false

  const vapidKey = await getVapidPublicKey()
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey),
  })

  const resp = await fetch(apiUrl('/api/push/subscribe-customer'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ subscription: subscription.toJSON() }),
  })

  if (!resp.ok) {
    console.error('[Push] Failed to save customer subscription')
    return false
  }

  console.log('[Push] Customer subscribed successfully')
  return true
}

/**
 * Unsubscribe customer from push notifications.
 */
export async function unsubscribeCustomerPush(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) {
      await subscription.unsubscribe()
    }

    await fetch(apiUrl('/api/push/subscribe-customer'), {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    })

    console.log('[Push] Customer unsubscribed')
    return true
  } catch (err) {
    console.error('[Push] Customer unsubscribe failed:', err)
    return false
  }
}
